import React, { useState, useEffect, useRef } from 'react';
import { Aperture, Zap, BarChart2, Shield, Users, Mail, Settings, ArrowUp } from 'lucide-react';

// Creates a glowing, blurred background element
const BlurGlow = ({ color, position, size, opacity }) => (
  <div
    className="hero-glow animate-pulse-bg"
    style={{
      background: color,
      width: size,
      height: size,
      top: position.top,
      left: position.left,
      zIndex: 0,
      opacity: `${opacity / 100}`,
    }}
  ></div>
);

const FeatureCard = ({ icon: Icon, title, description, accent }) => {
  const cardClass = `feature-card card-${accent}`;
  return (
    <div className={cardClass}>
      <div className="feature-card-glow"></div>
      <div className="card-content">
        <div className="card-icon">
          <Icon size={36} strokeWidth={2.5} />
        </div>
        <h3 className="card-title">{title}</h3>
        <p className="card-description">{description}</p>
      </div>
    </div>
  );
};

const NAV_ITEMS = [
  { name: 'Features', ref: 'features' },
  { name: 'How It Works', ref: 'how-it-works' },
  { name: 'Team', ref: 'team' },
  { name: 'Contact', ref: 'contact' },
];

const FEATURES_DATA = [
  { icon: Aperture, title: 'AI Photo Guidance', description: 'AVA directs users to capture standardized, high-integrity images, ensuring optimal claim data collection from the start.', accent: 'cyan' },
  { icon: Zap, title: 'Instant Claim Estimation', description: 'Deep learning models analyze damage in real-time to generate preliminary assessment reports, reducing settlement time dramatically.', accent: 'fuchsia' },
  { icon: Shield, title: 'Real-Time Fraud Detection', description: 'Computer vision instantly verifies image integrity, flagging staged accidents or image manipulation.', accent: 'green' },
  { icon: BarChart2, title: 'Claim Status & History', description: 'A digital dashboard provides users with transparent, real-time tracking and claim history.', accent: 'teal' },
];

const TEAM_DATA = [
  { 
    name: 'Devuja Weerakoon',
    role: 'Project Lead & Frontend Dev',
    imageUrl: "/images/devuja.jpeg",
    linkedin: "https://www.linkedin.com/in/devuja-weerakoon-81450732a/" 
  },
  { 
    name: 'Sukhitha Dharmasinghe',
    role: 'Mobile App Developer',
    imageUrl: "/images/sukitha.jpeg",
    linkedin: "https://www.linkedin.com/in/sukhitha-dharmasinghe/" 
  },
  { 
    name: 'Dulaj Subapanditha',
    role: 'AI & ML Engineer',
    imageUrl: "/images/dulaj.jpeg",
    linkedin: "https://www.linkedin.com/in/dulaj-subapanditha-74b804339/" 
  },
  { 
    name: 'Ometh Galhena',
    role: 'Mobile App Developer',
    imageUrl: "/images/ometh.jpeg",
    linkedin: "https://www.linkedin.com/in/ometh-g-717027281/" 
  },
  { 
    name: 'Saumya Liyanage',
    role: 'Backend Dev & ML Engineer',
    imageUrl: "/images/saumya.jpeg",
    linkedin: "https://www.linkedin.com/in/saumya-liyanage/" 
  },
  { 
    name: 'Adithya Pussewela',
    role: 'Frontend Dev & UI/UX Engineer',
    imageUrl: "/images/adithya.jpeg",
    linkedin: "https://www.linkedin.com/in/adithya-visal-8203ab35a/" 
  },
];


const App = () => {
  const sectionRefs = {
    features: useRef(null),
    'how-it-works': useRef(null),
    team: useRef(null),
    contact: useRef(null),
    hero: useRef(null),
    about: useRef(null),
  
  };

 

  const scrollToSection = (ref) => sectionRefs[ref].current?.scrollIntoView({ behavior: 'smooth' });

  const Hero = () => (
    <section ref={sectionRefs.hero} id="hero" className="hero scroll-fade">
      <BlurGlow color="var(--color-accent-cyan)" position={{ top: '-10%', left: '-10%' }} size="400px" opacity={20} />
      <BlurGlow color="var(--color-accent-fuchsia)" position={{ bottom: '0%', right: '-10%' }} size="450px" opacity={20} />

      <div className="hero-content">
        <h1 className="hero-title">
          <span className="logo-cyan"></span> Motor Insurance Claims Go <span className="hero-title-fuchsia">Virtual</span>
        </h1>
        <p className="hero-subtitle">
          AI driven inspections replace manual site visits to deliver faster, more transparent claim settlement.
        </p>
        <div className="hero-actions">
          <button onClick={() => scrollToSection('features')} className="btn-primary">Explore Features</button>
          <button onClick={() => scrollToSection('how-it-works')} className="btn-secondary">See How It Works</button>
        </div>

        <div className="hero-visual-wrapper">
          <div className="hero-visual">
            <Settings size={64} className="icon-settings animate-spin-slow" />
            <div className="hero-visual-text">
              {/* <Zap size={32} className="icon-zap animate-pulse-light" /> */}
              {/* <span className="animate-pulse-light">AI ASSESSMENT IN PROGRESS</span>
              <span className="detail">Guided Scan Complete... Processing</span> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

 const ABOUT_PRIVILEGES = [
  "Instant Claim Estimates",
  "AI-guided Damage Photos",
  "Fraud Detection Alerts",
  "Real-Time Claim Tracking",
  "Reduced Manual Paperwork",
];

const AboutUsSection = () => (
  <section ref={sectionRefs.about} id="about-us" className="section-about-us scroll-fade">
    <div className="container">
      <h2 className="section-title">About AVA</h2>
      <p className="section-subtitle">
        AVA is an AI-powered virtual assistant designed to streamline motor insurance claims. 
        By guiding users to capture accurate damage photos, analyzing them in real-time, 
        and providing instant claim estimates, AVA reduces delays, fraud, and manual work 
        for both insurers and customers.
      </p>

      {/* Privileges List */}
      <ul className="privileges-list">
  {ABOUT_PRIVILEGES.map((privilege, i) => (
    <li key={i}>
      <span className="privilege-tick">✔</span> {privilege}
    </li>
  ))}
</ul>
    </div>
  </section>
);



  const FeaturesSection = () => (
    <section ref={sectionRefs.features} id="features" className="section-features scroll-fade">
      <div className="container">
        <h2 className="section-title">AVA's Intelligence</h2>
        <p className="section-subtitle">Reducing delays, subjectivity, and fraud through deep learning and computer vision.</p>
        <div className="features-grid">
          {FEATURES_DATA.map((f, i) => <FeatureCard key={i} {...f} />)}
        </div>
      </div>
    </section>
  );

  const HowItWorksSection = () => (
   <section ref={sectionRefs['how-it-works']} id="how-it-works" className="section-how-it-works scroll-fade">
      <BlurGlow color="var(--color-accent-green)" position={{ top: '10%', right: '-10%' }} size="300px" opacity={10} />
      <div className="container">
        <h2 className="section-title">The 3 Step Digital Claim Flow</h2>
        <div className="step-flow">
          {['Capture & Validate', 'Analyze & Estimate', 'Settle & Proceed'].map((t, i) => (
            <div key={i} className="step-card">
              <div className="step-icon"><span className="step-number">{i + 1}</span></div>
              <h3 className="step-title">{t}</h3>
              <p className="step-description">
                {i === 0 && 'User captures verified damage photos with AI guidance.'}
                {i === 1 && 'AVA analyzes damage, checks fraud, and provides instant valuation.'}
                {i === 2 && 'Reports reach the insurer, accelerating approval and claim payout.'}
              </p>
            </div>
          ))}
          <div className="connector-line"></div>
        </div>
      </div>
    </section>
  );

  const TeamSection = () => (
    <section ref={sectionRefs.team} id="team" className="section-team scroll-fade">
      <div className="container">
        <h2 className="section-title">The Minds Behind AVA</h2>
        <div className="team-grid">
          {TEAM_DATA.map((member, idx) => (
          <div key={idx} className="team-member-card">
            <img src={member.imageUrl} className="member-image" alt={member.name} />
            <h3 className="member-name">{member.name}</h3>
            <p className="member-role">{member.role}</p>

            {/* LinkedIn Icon */}
            <a 
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="linkedin-btn"
            >
              <img 
                src="/images/linkedin.png" 
                alt="LinkedIn" 
                className="linkedin-icon" 
              />
            </a>
          </div>
        ))}
        </div>
      </div>
    </section>
  );

  const ContactSection = () => {
    const [status, setStatus] = useState('');
    const handleSubmit = (e) => {
      e.preventDefault();
      setStatus('Sending...');
      setTimeout(() => {
        setStatus('Message Sent! We will be in touch shortly.');
        e.target.reset();
      }, 2000);
    };
    return (
      <section ref={sectionRefs.contact} id="contact" className="section-contact scroll-fade">
        <BlurGlow color="var(--color-accent-fuchsia)" position={{ bottom: '10%', left: '10%' }} size="250px" opacity={15} />
        <div className="container">
          <h2 className="section-title">Connect With Our Team</h2>
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label className="form-label">Name</label><input type="text" required className="form-input" /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" required className="form-input" /></div>
              <div className="form-group"><label className="form-label">Message</label><textarea rows="4" required className="form-textarea"></textarea></div>
              <button type="submit" disabled={status.includes('Sent')} className="btn-submit">Send Message <Mail size={18} style={{ marginLeft: 6 }} /></button>
            </form>
            {status && <p className={`contact-status ${status.includes('Sent') ? 'status-green' : 'status-cyan'}`}>{status}</p>}
            <p className="contact-email">Email: <a href="mailto:info@ava-ai.lk">info@ava-ai.lk</a></p>
          </div>
        </div>
      </section>
    );
  };

  const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => {
      const handle = () => setScrolled(window.scrollY > 50);
      window.addEventListener('scroll', handle);
      return () => window.removeEventListener('scroll', handle);
    }, []);
    return (
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container header-content">
          
          {/* --- LOGO SECTION CHANGED HERE --- */}
          <a href="#hero" className="logo">
            {/* REPLACE THE SRC BELOW WITH YOUR LOGO URL */}
            <img 
              src="/images/logo.png" 
              alt="Logo" 
              className="logo-img" 
            />
            <span className="logo-text">AVA-Inspec</span>
          </a>
          {/* --------------------------------- */}

          {/* Hamburger Button */}
        <div className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* Navigation Menu */}
        <nav className={`nav ${menuOpen ? 'open' : ''}`}>
          {NAV_ITEMS.map(n => (
            <button
              key={n.name}
              className="nav-button"
              onClick={() => {
                scrollToSection(n.ref);
                setMenuOpen(false); // close menu after clicking
              }}
            >
              {n.name}
            </button>
          ))}
        </nav>

        </div>
      </header>
    );
  };

  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handle = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);

  useEffect(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    },
    { threshold: 0.2 }
  );

  Object.values(sectionRefs).forEach(ref => {
    if (ref.current) observer.observe(ref.current);
  });

  return () => observer.disconnect();
}, [sectionRefs]); 


  return (
    <div className="app-container">
      <Header />
      <main>
        <Hero />
        <AboutUsSection />
        <FeaturesSection />
        <HowItWorksSection />
        <TeamSection />
        <ContactSection />
      </main>

      {showScrollTop && (
        <button className="scroll-top-button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <ArrowUp size={24} />
        </button>
      )}

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} AVA - Automated Virtual Assistant. All rights reserved.</p>
        <p>Powered by Advanced Computer Vision for the Sri Lankan Motor Insurance Market.</p>
      </footer>

      {/* CSS ADDED BELOW */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Orbitron:wght@400..900&display=swap');

        /* ========== CSS Variables ========== */
        :root {
          --color-primary-dark: #0A0A1F;
          --color-gray-950: #0D0D1A;
          --color-gray-900: #13132A;
          --color-gray-800: #1F1F3D;
          --color-gray-700: #333366;
          --color-white: #FFFFFF;
          --color-gray-300: #CCCCCC;
          --color-gray-400: #999999;
          --color-accent-cyan: #00FFFF;
          --color-accent-fuchsia: #FF00FF;
          --color-accent-green: #39FF14;
          --color-accent-teal: #008080;
          --color-accent-indigo: #7B4BFF;
        }

        /* Scroll fade-in animation */
        .scroll-fade {
          opacity: 0;
          transform: translateY(100px);
          transition: opacity 1.2s ease-out, transform 1.2s ease-out;
        }

        .scroll-fade.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ========== Global Styles ========== */
        body {
          font-family: 'Inter', sans-serif;
          background: var(--color-primary-dark);
          color: var(--color-white);
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        h1, h2, h3 {
          font-family: 'Orbitron', sans-serif;
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1rem;
          position: relative;
          z-index: 10;
        }

        /* ========== Animations ========== */
        @keyframes pulse-bg {
          0%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.25;
          }
        }

        .animate-pulse-bg {
          animation: pulse-bg 10s infinite;
        }

        @keyframes pulse-light {
          0%, 100% {
            opacity: 1;
            text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
          }
          50% {
            opacity: 0.7;
            text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
          }
        }

        .animate-pulse-light {
          animation: pulse-light 2s infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin-slow {
          animation: spin 8s linear infinite;
        }

//         .section-about-us,
// .section-features,
// .section-how-it-works,
// .section-team,
// .section-contact {
//   padding-top: 8rem;   /* was 5rem */
//   padding-bottom: 8rem; /* was 5rem */
// }


        /* ========== Header ========== */
        .header {
          position: fixed;
          width: 100%;
          z-index: 50;
          transition: 0.3s;
          padding: 1rem 0;
        }

        .header.scrolled {
          background: rgba(19, 19, 42, 0.8);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--color-gray-800);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* Logo */
        .logo {
          display: flex;
          align-items: center;
          gap: 1px;
          text-decoration: none;
        }

        .logo-img {
          height: 30px;
          width: auto;
          object-fit: contain;
        }

        .logo-text {
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--color-accent-cyan), var(--color-accent-indigo));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: -0.02em;
  text-shadow: 0 0 12px rgba(123, 75, 255, 0.4);
}

.logo-img {
  height: 60px; /* increase from 40px */
  width: auto;
  object-fit: contain;
}

        .logo-cyan {
          color: var(--color-accent-cyan);
          filter: drop-shadow(0 0 4px rgba(0, 255, 255, 0.5));
        }

        .logo-fuchsia {
          color: var(--color-accent-fuchsia);
        }

        /* Navigation */
        .nav {
          display: none;
          gap: 2rem;
        }

        .nav-button {
          color: var(--color-gray-300);
          font-weight: 500;
          letter-spacing: 0.05em;
          background: none;
          border: none;
          cursor: pointer;
          position: relative;
          padding-bottom: 5px;
        }

        .nav-button:hover {
          color: var(--color-accent-cyan);
        }

        .nav-underline {
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 2px;
          background: var(--color-accent-cyan);
          transform: scaleX(0);
          transition: 0.3s;
        }

        .nav-button:hover .nav-underline {
          transform: scaleX(1);
        }

        /* ========== Hero Section ========== */
        .hero {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 4rem 1rem 1rem;
  position: relative;
  overflow: hidden;
}

/* Blur + darkness overlay */
.hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  z-index: 1;
}

/* Make hero content stay above overlay */
.hero > * {
  position: relative;
  z-index: 2;
}

/* Glow elements */
.hero-glow {
  position: absolute;
  border-radius: 9999px;
  filter: blur(48px);
  mix-blend-mode: screen;
}


        .hero-title {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 8rem;
        }

        .hero-title-fuchsia {
          color: var(--color-accent-fuchsia);
        }

        .hero-subtitle {
          color: var(--color-gray-300);
          margin-bottom: 4rem;
          font-size: 1.25rem;
          max-width: 56rem;
          margin: auto;
        }

        .hero-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 4rem;
          margin-top: 4rem;
        }

        /* Buttons */
        .btn-primary {
          background: var(--color-accent-cyan);
          color: #0F172A;
          font-weight: 700;
          padding: 0.75rem 2rem;
          border-radius: 9999px;
          transition: 0.3s;
        }

        .btn-secondary {
          border: 2px solid var(--color-accent-fuchsia);
          color: var(--color-accent-fuchsia);
          font-weight: 700;
          padding: 0.75rem 2rem;
          border-radius: 9999px;
          background: none;
          transition: 0.3s;
        }

        .btn-primary:hover,
        .btn-secondary:hover {
          transform: scale(1.05);
        }

        .btn-secondary:hover {
          background: var(--color-accent-fuchsia);
          color: #0F172A;
        }

        /* ========== About Section ========== */
        .section-about-us {
          padding: 5rem 0;
          background: rgba(19, 19, 42, 0.85);
        }

        .section-about-us .section-title {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 3rem;
          color: var(--color-accent-fuchsia);
        }

        .section-about-us .section-subtitle {
          text-align: center;
          color: var(--color-white-400);
          max-width: 48rem;
          margin: auto;
          font-size: 1.21rem;
          margin-bottom: 8rem;
          line-height: 1.8;
        }

        .privileges-list {
  margin-top: 2rem;
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: center; /* center grid */
  gap: 1.5rem 3rem; /* row gap / column gap */
}

.privileges-list li {
  font-size: 1.125rem;
  color: var(--color-white);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.privilege-tick {
  color: var(--color-accent-green);
  font-weight: bold;
  font-size: 1.25rem;
}

/* make the second row centered for 2 items */
.privileges-list li:nth-child(4),
.privileges-list li:nth-child(5) {
  justify-self: center;
}




        /* ========== Sections ========== */
        .section-features,
        .section-how-it-works,
        .section-team,
        .section-contact {
          padding: 5rem 0;
        }

        .section-features {
          background: rgba(13, 13, 26, 0.7);
        }

        .section-title {
          text-align: center;
          font-size: 2.25rem;
          font-weight: 800;
          margin-bottom: 2rem;
        }

        .section-subtitle {
          text-align: center;
          color: var(--color-gray-400);
          margin-bottom: 5rem;
          max-width: 48rem;
          margin: auto;
        }

        /* ========== Features ========== */
        .features-grid {
          display: grid;
          gap: 2rem;
          margin-top: 8rem;
        }

        .feature-card {
          padding: 1.5rem;
          background: rgba(31, 31, 61, 0.5);
          border: 1px solid var(--color-gray-700);
          border-radius: 0.75rem;
          transition: 0.3s;
          position: relative;
        }

        .feature-card:hover {
          transform: scale(1.03);
        }

        .feature-card-glow {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: 0.3s;
        }

        .feature-card:hover .feature-card-glow {
          opacity: 0.1;
        }

        .card-cyan .card-icon {
          color: var(--color-accent-cyan);
        }

        .card-fuchsia .card-icon {
          color: var(--color-accent-fuchsia);
        }

        .card-green .card-icon {
          color: var(--color-accent-green);
        }

        .card-teal .card-icon {
          color: var(--color-accent-teal);
        }

        /* ========== How It Works ========== */
        .section-how-it-works {
          background: var(--color-gray-900);
        }

        .step-flow {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3rem;
          margin-top: 5rem;
        }

        .step-card {
          padding: 1.5rem;
          background: rgba(31, 31, 61, 0.6);
          border: 1px solid var(--color-gray-700);
          border-radius: 0.75rem;
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
          text-align: center;
          width: 100%;
          max-width: 330px;
        }

        .step-icon {
          width: 4rem;
          height: 4rem;
          border: 4px solid var(--color-accent-cyan);
          border-radius: 9999px;
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 1rem;
        }

        .step-number {
          font-size: 1.875rem;
          color: var(--color-accent-cyan);
        }

        /* ========== Team ========== */
        .section-team {
          background: rgba(13, 13, 26, 0.7);
        }

        .team-grid {
          display: grid;
          gap: 2rem;
          margin-top: 6rem;
        }

        .team-member-card {
          padding: 1.5rem;
          background: rgba(31, 31, 61, 0.5);
          border-radius: 0.75rem;
          border: 1px solid var(--color-gray-700);
          text-align: center;
          transition: 0.3s;
        }

        .team-member-card:hover {
          transform: scale(1.03);
        }

        .member-image {
          width: 8rem;
          height: 8rem;
          border-radius: 9999px;
          border: 4px solid var(--color-accent-cyan);
          object-fit: cover;
          margin-bottom: 1rem;
        }

        .linkedin-btn {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin-top: 0.75rem;
      }

      .linkedin-icon {
        width: 32px;
        height: 32px;
        transition: 0.25s ease;
      }

      .linkedin-icon:hover {
        transform: scale(1.15);
        filter: drop-shadow(0 0 4px #0A66C2);
      }


        /* ========== Contact ========== */
        .section-contact {
  background: var(--color-gray-900);
  padding: 6rem 0; /* increase vertical space */
}

.contact-form-wrapper {
  background: rgba(31, 31, 61, 0.6);
  padding: 3rem; /* increase padding inside the form */
  border: 1px solid var(--color-gray-700);
  border-radius: 0.75rem;
  max-width: 600px; /* make form wider */
  margin: auto;
}

.form-input,
.form-textarea {
  width: 100%;
  background: var(--color-gray-700);
  color: white;
  padding: 1rem; /* bigger input boxes */
  border-radius: 0.375rem;
  border: 1px solid var(--color-gray-600);
}

.btn-submit {
  width: 100%;
  background: #EC4899;
  color: white;
  font-weight: 700;
  padding: 1rem; /* bigger button */
  border-radius: 0.375rem;
  transition: 0.3s;
  margin-top: 0.5rem;
}


        /* ========== Footer ========== */
        .footer {
          text-align: center;
          background: rgba(13, 13, 26, 0.9);
          padding: 1.5rem;
          color: gray;
        }

        /* ========== Scroll Top Button ========== */
        .scroll-top-button {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          background: #DB2777;
          padding: 0.75rem;
          border-radius: 9999px;
          border: none;
          cursor: pointer;
        }

        /* ========== Media Queries ========== */
        @media (min-width: 640px) {
          .hero-title {
            font-size: 3.75rem;
          }

          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .team-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 768px) {
          .nav {
            display: flex;
          }

          .team-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .features-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .step-flow {
            flex-direction: row;
            justify-content: space-between;
          }

          .step-card {
            width: 33.33%;
          }
        }
          

      /* ========== Mobile Responsive (<640px) ========== */
@media (max-width: 639px) {

  /* Hero Section */
  .hero-title {
    font-size: 2rem;
    margin-bottom: 3rem;
  }

  .hero-subtitle {
    font-size: 1rem;
    margin-bottom: 2.5rem;
  }

  .hero-actions {
    flex-direction: column;
    gap: 0.75rem;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%; /* full-width buttons */
  }

  /* About Section */
  .section-about-us .section-title {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }

  .section-about-us .section-subtitle {
    font-size: 0.95rem;
    line-height: 1.4;
    max-width: 95%;
    margin-bottom: 3rem;
  }

  .privileges-list {
    grid-template-columns: repeat(2, 1fr); /* 2 columns instead of 3 */
    gap: 1rem;
    justify-items: center;
  }

  /* Features Section */
  .features-grid {
    grid-template-columns: 1fr; /* stack feature cards */
    gap: 1.5rem;
    margin-top: 4rem;
  }

  .feature-card {
    padding: 1rem;
  }

  /* How It Works Section */
  .step-flow {
    flex-direction: column;
    gap: 2rem;
    margin-top: 4rem;
  }

  .step-card {
    width: 100%;
    padding: 1rem;
  }

  .step-icon {
    width: 3rem;
    height: 3rem;
  }

  .step-number {
    font-size: 1.5rem;
  }

  /* Team Section */
  .team-grid {
    grid-template-columns: 1fr; /* stack team members */
    gap: 1.5rem;
    margin-top: 4rem;
  }

  .team-member-card {
    padding: 1rem;
  }

  .member-image {
    width: 6rem;
    height: 6rem;
  }

  /* Contact Section */
  .contact-form-wrapper {
    padding: 2rem;
    width: 90%; /* fit small screens */
  }

  .form-input,
  .form-textarea {
    padding: 0.75rem;
  }

  .btn-submit {
    padding: 0.75rem;
  }

  /* Section Titles */
  .section-title {
    font-size: 1.75rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  /* ========== Mobile Nav ========== */
  .nav {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 70px; /* below header */
    right: 0;
    background: rgba(13, 13, 26, 0.95);
    width: 60%;
    max-width: 250px;
    height: calc(100% - 70px);
    padding: 2rem 1rem;
    gap: 1rem;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
    z-index: 100;
  }

  .nav.open {
    transform: translateX(0);
  }

  .nav-button {
    font-size: 1.2rem;
    color: var(--color-white);
    text-align: left;
  }

  .nav-underline {
    display: none; /* remove underline on mobile */
  }

  /* Hamburger button */
  .hamburger {
    display: block;
    cursor: pointer;
    position: relative;
    width: 24px;
    height: 18px;
  }

  .hamburger span {
    position: absolute;
    height: 3px;
    width: 100%;
    background: white;
    border-radius: 2px;
    left: 0;
    transition: all 0.3s ease;
  }

  .hamburger span:nth-child(1) { top: 0; }
  .hamburger span:nth-child(2) { top: 7px; }
  .hamburger span:nth-child(3) { top: 14px; }
}

}

      `}</style>
    </div>
  );
};

export default App;